import { openDB, DBSchema, IDBPDatabase } from 'idb';

interface StoreDB extends DBSchema {
  'store-state': {
    key: string;
    value: any;
  };
}

const DB_NAME = 'hiiker-plan-trail';
const STORE_NAME = 'store-state';
const DB_VERSION = 1;
const STATE_KEY = 'vuex-state';

let dbPromise: Promise<IDBPDatabase<StoreDB>> | null = null;

/**
 * Initialize and return the IndexedDB database instance
 */
function getDB(): Promise<IDBPDatabase<StoreDB>> {
  if (!dbPromise) {
    dbPromise = openDB<StoreDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Create the object store if it doesn't exist
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      },
    });
  }
  return dbPromise;
}

/**
 * Save the Vuex state to IndexedDB
 * @param state The Vuex state to save
 */
export async function saveStateToIndexedDB(state: any): Promise<void> {
  try {
    const db = await getDB();
    await db.put(STORE_NAME, state, STATE_KEY);
  } catch (error) {
    console.error('Failed to save state to IndexedDB:', error);
    // Optionally: Try to clear old data and retry if quota exceeded
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      console.warn('IndexedDB quota exceeded. Attempting to clear old data...');
      try {
        await clearStateFromIndexedDB();
        const db = await getDB();
        await db.put(STORE_NAME, state, STATE_KEY);
      } catch (retryError) {
        console.error('Failed to save state even after clearing:', retryError);
      }
    }
  }
}

/**
 * Load the Vuex state from IndexedDB
 * @returns The saved state, or null if not found
 */
export async function loadStateFromIndexedDB(): Promise<any | null> {
  try {
    const db = await getDB();
    const state = await db.get(STORE_NAME, STATE_KEY);
    return state || null;
  } catch (error) {
    console.error('Failed to load state from IndexedDB:', error);
    return null;
  }
}

/**
 * Clear the saved state from IndexedDB and localStorage
 */
export async function clearStateFromIndexedDB(): Promise<void> {
  try {
    const db = await getDB();
    await db.delete(STORE_NAME, STATE_KEY);
    // Also clear localStorage for completeness
    localStorage.removeItem('store');
  } catch (error) {
    console.error('Failed to clear state from IndexedDB:', error);
  }
}

/**
 * Migrate data from localStorage to IndexedDB (one-time migration)
 */
export async function migrateFromLocalStorage(): Promise<void> {
  try {
    const localStorageData = localStorage.getItem('store');
    if (localStorageData) {
      const state = JSON.parse(localStorageData);
      await saveStateToIndexedDB(state);
      // Remove from localStorage after successful migration
      localStorage.removeItem('store');
      console.log('Successfully migrated data from localStorage to IndexedDB');
    }
  } catch (error) {
    console.error('Failed to migrate from localStorage:', error);
  }
}
